import { Location } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { cpf } from 'cpf-cnpj-validator';
import { forkJoin, Subscription } from 'rxjs';
import { tratarIsNullOrEmpty, validarCPF } from 'src/app/core/utils/utilities';
import { validarSenha } from 'src/app/core/validators/validarSenha';
import {
  ICreateFuncionario,
  IGetFuncionario
} from 'src/app/services/FuncionarioService/funcionario.model';
import { FuncionarioService } from 'src/app/services/FuncionarioService/funcionario.service';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';
import {
  IGetTipoPerfil,
  IGetTipoPerfilList
} from 'src/app/services/TipoPerfil/tipoPerfil.model';
import { TipoPerfilService } from 'src/app/services/TipoPerfil/tipoPerfil.service';
import { ViaCepService } from 'src/app/services/ViaCep/viacep.service';
import Swal from 'sweetalert2';

type IOptions = {
  name: string;
  value: string | number;
};

@Component({
  selector: 'app-cadastro-funcionario',
  templateUrl: './cadastro-funcionario.component.html',
  styleUrl: './cadastro-funcionario.component.scss'
})
export class CadastroFuncionarioComponent {
  private subscription$ = new Subscription();
  disabledBotaoUsuario: boolean = true;
  form: FormGroup = this._inicializarFormulario();
  title: string = 'Cadastrar Funcionário';
  isLoading: boolean = false;
  statusInput: boolean = false;
  activeRoute: string = '';
  pkFuncionario: number = 0;
  tipoPerfilOptions: IOptions[] = [
    {
      name: '',
      value: 0
    }
  ];
  turnoOptions: IOptions[] = [
    { name: '07 às 19h', value: 1 },
    { name: '19h às 07h', value: 2 },
    { name: '24h', value: 3 }
  ];
  isLengthValid = false;
  hasNumber = false;
  hasLowercase = false;
  hasUppercase = false;
  hasSpecialChar = false;
  passwordsMatch = false;
  isValid = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private funcionarioService: FuncionarioService,
    private tipoPerfilService: TipoPerfilService,
    private viaCepService: ViaCepService,
    private location: Location,
    public swalAlert: SwalAlertService
  ) {}

  private _inicializarFormulario() {
    const form = this.fb.group(
      {
        NM_FUNC: ['', [Validators.required]],
        TX_CPF_FUNC: [null],
        TX_MAIL_FUNC: ['', [Validators.required]],
        TX_TELE_FUNC: [null],
        TX_ENDE_CEP_FUNC: [null],
        TX_ENDE_ESTD_FUNC: [null],
        TX_ENDE_CDDE_FUNC: [null],
        TX_ENDE_RUA_FUNC: [null],
        TX_USER_NAME: [null, Validators.required],
        FK_PERF: ['', [Validators.required]],
        NU_TRNO_FUNC: ['', [Validators.required]],
        TX_SENH_FUNC: ['', [Validators.required, Validators.maxLength(15)]],
        TX_SENH_FUNC_CNFRM: [
          '',
          [Validators.required, Validators.maxLength(15)]
        ],
        NU_RGST: [null, [Validators.required]],
        NU_PIS_PASEP: [null]
      },
      { validators: [validarSenha('TX_SENH_FUNC', 'TX_SENH_FUNC_CNFRM')] }
    );
    return form;
  }

  ngOnInit(): void {
    const observables = [this.getRolePerfil()];

    forkJoin(observables).subscribe(() => {
      this.stateForms();
    });
  }

  createPayload(): ICreateFuncionario {
    return {
      TX_MAIL_FUNC: tratarIsNullOrEmpty(
        this.form.controls['TX_MAIL_FUNC'].value
      ),
      TX_SENH_FUNC: tratarIsNullOrEmpty(
        this.form.controls['TX_SENH_FUNC'].value
      ),
      NM_FUNC: tratarIsNullOrEmpty(this.form.controls['NM_FUNC'].value),
      TX_CPF_FUNC: tratarIsNullOrEmpty(this.form.controls['TX_CPF_FUNC'].value),
      TX_TELE_FUNC: tratarIsNullOrEmpty(
        this.form.controls['TX_TELE_FUNC'].value
      ),
      NU_STTS_FUNC: 1,
      FK_PERF: this.form.controls['FK_PERF'].value ?? 0,
      TX_ENDE_CEP_FUNC: tratarIsNullOrEmpty(
        this.form.controls['TX_ENDE_CEP_FUNC'].value
      ),
      TX_ENDE_RUA_FUNC: tratarIsNullOrEmpty(
        this.form.controls['TX_ENDE_RUA_FUNC'].value
      ),
      TX_ENDE_CDDE_FUNC: tratarIsNullOrEmpty(
        this.form.controls['TX_ENDE_CDDE_FUNC'].value
      ),
      TX_ENDE_ESTD_FUNC: tratarIsNullOrEmpty(
        this.form.controls['TX_ENDE_ESTD_FUNC'].value
      ),
      NU_RGST: tratarIsNullOrEmpty(this.form.controls['NU_RGST'].value),
      NU_PIS_PASEP: tratarIsNullOrEmpty(
        this.form.controls['NU_PIS_PASEP'].value
      ),
      TX_USER_NAME: tratarIsNullOrEmpty(
        this.form.controls['TX_USER_NAME'].value
      ),
      NU_TRNO_FUNC: Number(this.form.controls['NU_TRNO_FUNC'].value)
    };
  }

  postarDados() {
    const payload = this.createPayload();
    this.isLoading = true;

    if (this.form.invalid) {
      this.isLoading = false;
      this.swalAlert.showAlert(
        'Atenção!',
        'Verifique se todos os campos obrigatórios foram preenchidos corretamente.',
        'warning'
      );
      return;
    }

    if (
      this.form.controls['TX_CPF_FUNC'].value &&
      !cpf.isValid(String(this.form.controls['TX_CPF_FUNC'].value))
    ) {
      this.isLoading = false;
      this.swalAlert.showAlert(
        'Atenção!',
        'O CPF informado é inválido.',
        'warning'
      );
      return;
    }

    if (this.title.includes('Editar')) {
      this.isLoading = true;
      this.funcionarioService
        .EditarFuncionario(this.pkFuncionario, payload)
        .subscribe(
          response => {
            this.isLoading = false;
            this.router.navigate(['listagem/funcionario']);
          },
          error => {
            this.isLoading = false;
          }
        );
      return;
    }

    this.funcionarioService.CreateFuncionario(payload).subscribe(
      response => {
        this.isLoading = false;
        this.router.navigate(['listagem/funcionario']);
      },
      error => {
        this.isLoading = false;
      }
    );
  }

  goTo(route: string) {
    this.router.navigate([route]);
    setTimeout(() => {
      this.activeRoute = this.router.url;
      this.cd.detectChanges();
    }, 200);
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  atualizarCampo(campo: string, valor: any): void {
    if (this.form.controls[campo].value !== valor) {
      this.form.controls[campo].setValue(valor);
    }
  }

  async getRolePerfil(): Promise<void> {
    this.isLoading = true;

    try {
      const response = (await this.tipoPerfilService
        .GetTipoPerfil()
        .toPromise()) as IGetTipoPerfilList;

      this.tipoPerfilOptions = response.map(x => ({
        name: x.NM_PERF,
        value: x.PK_PERF
      }));
    } catch (error) {
      console.error('Erro ao buscar tipo perfil:', error);
    } finally {
      this.isLoading = false;
    }
  }

  escolherOpcao(campo: string, options: any[], value: any): void {
    const selecionado = options.find(opcao => opcao?.value === Number(value));
    this.atualizarCampo(campo, selecionado?.value);
  }

  escolherTipoPerfil(value: any): void {
    this.escolherOpcao('FK_PERF', this.tipoPerfilOptions, value ?? 0);
  }

  escolherTurno(value: any): void {
    this.escolherOpcao('NU_TRNO_FUNC', this.turnoOptions, value ?? 0);
  }

  consultaCEP() {
    const cep = String(this.form.controls['TX_ENDE_CEP_FUNC'].value);

    if (cep.length >= 8) {
      this.viaCepService.GetBuscarCep(cep).subscribe(response => {
        this.form.controls['TX_ENDE_CDDE_FUNC'].setValue(response.localidade);
        this.form.controls['TX_ENDE_ESTD_FUNC'].setValue(response.uf);
        this.form.controls['TX_ENDE_RUA_FUNC'].setValue(response.logradouro);
      });
    }
  }

  confirmacaoSenha() {
    const senha = this.form.controls['TX_SENH_FUNC'].value;
    const novaSenha = this.form.controls['TX_SENH_FUNC_CNFRM'].value;

    this.isLengthValid = senha.length >= 8;
    this.hasNumber = /\d/.test(senha);
    this.hasLowercase = /[a-z]/.test(senha);
    this.hasUppercase = /[A-Z]/.test(senha);
    this.hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(senha);
    this.passwordsMatch = senha === novaSenha;

    if (this.isMode()) {
      if (
        this.isLengthValid &&
        this.hasNumber &&
        this.hasLowercase &&
        this.hasUppercase &&
        this.hasSpecialChar &&
        this.passwordsMatch
      ) {
        this.isValid = true;
      } else {
        this.isValid = false;
      }
    }
  }

  async stateForms() {
    this.route.queryParams.subscribe(params => {
      if (params && Object.keys(params).length > 0) {
        this.title = 'Editar Funcionário';
        this.funcionarioService
          .GetFuncionarioById(Number(params['codigo']))
          .subscribe((response: IGetFuncionario) => {
            this.pkFuncionario = response.PK_FUNC;
            this.form.controls['NM_FUNC'].setValue(response.NM_FUNC ?? '');
            this.form.controls['TX_CPF_FUNC'].setValue(
              response.TX_CPF_FUNC ?? ''
            );
            this.form.controls['TX_MAIL_FUNC'].setValue(
              response.TX_MAIL_FUNC ?? ''
            );
            this.form.controls['TX_TELE_FUNC'].setValue(
              response.TX_TELE_FUNC ?? ''
            );
            this.form.controls['TX_ENDE_CEP_FUNC'].setValue(
              response.TX_ENDE_CEP_FUNC ?? ''
            );
            this.form.controls['TX_ENDE_ESTD_FUNC'].setValue(
              response.TX_ENDE_ESTD_FUNC ?? ''
            );
            this.form.controls['TX_ENDE_CDDE_FUNC'].setValue(
              response.TX_ENDE_CDDE_FUNC ?? ''
            );
            this.form.controls['TX_ENDE_RUA_FUNC'].setValue(
              response.TX_ENDE_RUA_FUNC ?? ''
            );
            this.form.controls['FK_PERF'].setValue(response.FK_PERF ?? '');
            this.form.controls['NU_TRNO_FUNC'].setValue(
              response.NU_TRNO_FUNC ?? ''
            );
            this.form.controls['TX_SENH_FUNC'].setValue(
              response.TX_SENH_FUNC ?? ''
            );
            this.form.controls['TX_SENH_FUNC_CNFRM'].setValue(
              response.TX_SENH_FUNC ?? ''
            );
            this.form.controls['NU_RGST'].setValue(response.NU_RGST ?? '');
            this.form.controls['NU_PIS_PASEP'].setValue(
              response.NU_PIS_PASEP ?? ''
            );
            this.form.controls['TX_USER_NAME'].setValue(
              response.TX_USER_NAME ?? ''
            );
            this.form.controls['TX_SENH_FUNC'].removeValidators;
            this.form.controls['TX_SENH_FUNC_CNFRM'].removeValidators;
          });

        this.isValid = true;
        if (params['modoFuncionario'] == 'Visualizar') {
          this.title = 'Visualizar Funcionário';
          this.statusInput = true;
          this.form.disable();
          this.disabledBotaoUsuario = true;
        }
      }
    });

    this.isMode();
  }

  isMode(): boolean {
    const mode = this.location.path();
    if (!mode.includes('Editar')) {
      return true;
    }

    this.form.controls['TX_SENH_FUNC'].clearValidators();
    this.form.controls['TX_SENH_FUNC_CNFRM'].clearValidators();
    this.form.controls['TX_SENH_FUNC'].setErrors(null);
    this.form.controls['TX_SENH_FUNC_CNFRM'].setErrors(null);
    this.form.clearValidators();
    this.isValid = true;

    return false;
  }
}
